// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XAvatar from "@bg/App/Components/XAvatar/XAvatar";
import { CustomAppState } from "@bg/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import Link from "@bg/MobiusOverrides/Link/Link";
import { ModalPresentationProps } from "@bg/MobiusOverrides/Modal/Modal";
import { LinkPresentationProps } from "@bgs/MobiusOverrides/Link/Link";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useState } from "react";
import { shallowEqual } from "react-redux";

interface OwnProps {
    associatedSales?: AssociatedSaleRep[];
    associatedSalesRepNumber?: string;
    disabled?: boolean;
    extendedStyles?: AssociatedSalesRepModalStyles;
}

interface AssociatedSaleRep {
    Email?: string;
    Image?: string;
    Name?: string;
    Phone?: string;
    SalesRepNum: string;
    UserProfileFirstName?: string;
    UserProfileLastName?: string;
}

export interface AssociatedSalesRepModalStyles {
    modal?: ModalPresentationProps;
    salesRepItem?: InjectableCss;
    salesRepContainer?: InjectableCss;
    infoItem?: InjectableCss;
    image?: LazyImageProps;
    name?: TypographyProps;
    email?: TypographyProps;
    actionsContainer?: InjectableCss;
    skipLink?: LinkPresentationProps;
    selectAssociateButton?: ButtonPresentationProps;
    radioGroupStyles?: RadioGroupPresentationProps;
    radio?: RadioPresentationProps;
}

export const associatedSalesRepModalStyles: AssociatedSalesRepModalStyles = {};

const styles = associatedSalesRepModalStyles;
const AssociatedSalesRepModal = ({ associatedSales, associatedSalesRepNumber, disabled }: OwnProps) => {
    const [selectedAssociatedSalesRep, setSelectedAssociatedSalesRep] = useState(
        associatedSales?.some((item: AssociatedSaleRep) => item.SalesRepNum === associatedSalesRepNumber)
            ? associatedSalesRepNumber
            : "",
    );

    const { isSmallMobileScreen, isAssociatedSalesRepModalOpen } = useAppSelector(
        state => ({
            isSmallMobileScreen: (state as CustomAppState).commonActions["isSmallMobileScreen"],
            isAssociatedSalesRepModalOpen: (state as CustomAppState).xOnePageCheckout.isAssociatedSalesRepModalOpen,
        }),
        shallowEqual,
    );
    const dispatch = useAppDispatch();

    if (!associatedSales) {
        return null;
    }

    const modalCloseHandler = () => {
        dispatch({
            type: "xOnePageCheckout/setIsAssociatedSalesRepModalOpen",
            isOpen: false,
        });
    };

    const skipLink = (
        <Link onClick={modalCloseHandler} {...styles.skipLink}>
            {translate("Skip & Continue to Checkout")}
        </Link>
    );

    const submitHandler = () => {
        dispatch({
            type: "xCheckoutAssociatedSalesRep/update",
            updateAssociatedSalesRep: true,
            associatedSalesRep: selectedAssociatedSalesRep,
        });
        modalCloseHandler();
    };

    return (
        <Modal
            {...styles.modal}
            isOpen={isAssociatedSalesRepModalOpen && !disabled}
            handleClose={modalCloseHandler}
            headline={translate("Did Anyone Assist You?")}
        >
            {isSmallMobileScreen && skipLink}

            <RadioGroup
                value={selectedAssociatedSalesRep}
                onChangeHandler={(event: ChangeEvent<HTMLInputElement>) => {
                    setSelectedAssociatedSalesRep(event.currentTarget.value);
                }}
                {...styles.radioGroupStyles}
            >
                {associatedSales.map((item: AssociatedSaleRep) => (
                    <Radio key={item.SalesRepNum} {...styles.radio} value={item.SalesRepNum}>
                        <StyledWrapper
                            {...styles.salesRepContainer}
                            {...{ selected: selectedAssociatedSalesRep === item.SalesRepNum }}
                        >
                            {item.Image ? (
                                <LazyImage {...styles.image} src={item.Image} altText={item.Name} />
                            ) : (
                                <XAvatar
                                    firstName={item.UserProfileFirstName}
                                    lastName={item.UserProfileLastName}
                                    width={isSmallMobileScreen ? "88" : "116"}
                                    height={isSmallMobileScreen ? "88" : "116"}
                                    square={true}
                                />
                            )}
                            <StyledWrapper {...styles.infoItem}>
                                <StyledWrapper {...styles.name}>
                                    <Typography>{!!item.UserProfileLastName && item.UserProfileFirstName}</Typography>
                                    <Typography>{!!item.UserProfileLastName && item.UserProfileLastName}</Typography>
                                </StyledWrapper>
                                {!!item.Email && <Typography {...styles.email}>{item.Email}</Typography>}
                            </StyledWrapper>
                        </StyledWrapper>
                    </Radio>
                ))}
            </RadioGroup>

            <StyledWrapper {...styles.actionsContainer}>
                {!isSmallMobileScreen && skipLink}
                <Button
                    {...styles.selectAssociateButton}
                    disabled={!selectedAssociatedSalesRep}
                    onClick={submitHandler}
                >
                    {translate("Select Associate")}
                </Button>
            </StyledWrapper>
        </Modal>
    );
};

export default AssociatedSalesRepModal;
